// Wundersphere micrographics — ported from brand guidelines

export function Barcode({ seed = "WS001", width = 120, height = 28, color = "currentColor" }) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const bars: { x: number; w: number }[] = [];
  let x = 0, i = 0;
  while (x < width) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    const w = 1 + (h % 4);
    const gap = 1 + ((h >> 3) % 3);
    if (i % 2 === 0) bars.push({ x, w });
    x += w + gap;
    i++;
  }
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {bars.map((b, k) => <rect key={k} x={b.x} y="0" width={b.w} height={height} fill={color} />)}
    </svg>
  );
}

export function DataMatrix({ size = 36, cells = 10, seed = "WS", color = "currentColor" }) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const cs = size / cells;
  const rects: React.ReactNode[] = [];
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      h = (h * 1103515245 + 12345) & 0x7fffffff;
      const finder = (x === 0 && y < cells - 1) || (y === cells - 1 && x < cells - 1);
      const alt = (x + y) % 2 === 0 && (x === cells - 1 || y === 0);
      if (finder || alt || (h & 1)) rects.push(<rect key={`${x}-${y}`} x={x * cs} y={y * cs} width={cs} height={cs} fill={color} />);
    }
  }
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{rects}</svg>;
}

export function CertStamp({ label = "E1", size = 36, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" stroke={color} strokeWidth="1">
      <circle cx="18" cy="18" r="16" />
      <text x="18" y="22" fontFamily="var(--font-mono)" fontSize="12" fontWeight="500" textAnchor="middle" fill={color} stroke="none">{label}</text>
    </svg>
  );
}

export function TriArcStamp({ size = 36, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" stroke={color} strokeWidth="1">
      <circle cx="18" cy="18" r="16" />
      <path d="M 24 10 A 10 10 0 1 0 24 26" />
      <path d="M 21 13 A 6 6 0 1 0 21 23" />
      <path d="M 18 16 A 3 3 0 1 0 18 20" />
    </svg>
  );
}

export function Reticle({ size = 24, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1">
      <circle cx="12" cy="12" r="6" />
      <line x1="12" y1="0" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="24" />
      <line x1="0" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="24" y2="12" />
      <circle cx="12" cy="12" r="1" fill={color} />
    </svg>
  );
}

export function CompassStar({ size = 24, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1">
      <line x1="12" y1="0" x2="12" y2="24" />
      <line x1="0" y1="12" x2="24" y2="12" />
      <line x1="3" y1="3" x2="21" y2="21" strokeDasharray="1 2" />
      <line x1="21" y1="3" x2="3" y2="21" strokeDasharray="1 2" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

export function HalftoneStrip({ width = 200, height = 20, color = "currentColor", density = 40 }) {
  const dots: React.ReactNode[] = [];
  const rows = Math.max(1, Math.floor(height / 4));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < density; c++) {
      const t = c / density;
      const radius = 0.5 + Math.abs(Math.sin(t * Math.PI)) * 1.2;
      dots.push(<circle key={`${r}-${c}`} cx={c * (width / density) + 1} cy={r * 4 + 2} r={radius} fill={color} />);
    }
  }
  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>{dots}</svg>;
}

export function RegMark({ size = 18, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" stroke={color} strokeWidth="0.8">
      <circle cx="9" cy="9" r="6" />
      <circle cx="9" cy="9" r="2" />
      <line x1="9" y1="0" x2="9" y2="18" />
      <line x1="0" y1="9" x2="18" y2="9" />
    </svg>
  );
}

export function CoordBlock({ lat = "51.5074° N", lng = "0.1278° W", label = "NODE 001", color = "var(--ink)" }) {
  return (
    <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1.4, color }}>
      <div style={{ opacity: 0.5 }}>◉ {label}</div>
      <div>{lat}</div>
      <div>{lng}</div>
    </div>
  );
}

export function SpecTable({ rows = [] as [string, string][], color = "var(--ink)" }) {
  return (
    <table style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", color, borderCollapse: "collapse" }}>
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i}>
            <td style={{ opacity: 0.55, paddingRight: 12, paddingBottom: 2, verticalAlign: "top" }}>{k}</td>
            <td style={{ paddingBottom: 2, verticalAlign: "top" }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Serial({ prefix = "WS", n = "0017", suffix = "/ 26" }) {
  return (
    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}>
      {prefix}—{n} <span style={{ opacity: 0.5 }}>{suffix}</span>
    </span>
  );
}

export function MeridianMark({ size = 120, color = "currentColor", sw = 1, bg = "var(--paper)" }) {
  const id = `mm-${size}-${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" stroke={color} strokeWidth={sw}>
      <defs>
        <clipPath id={id}><circle cx="60" cy="60" r="56" /></clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        <circle cx="60" cy="60" r="56" fill={color} stroke="none" />
        <circle cx="74" cy="56" r="54" fill={bg} stroke="none" />
      </g>
      <circle cx="60" cy="60" r="56" />
      <ellipse cx="60" cy="60" rx="18" ry="56" />
      <ellipse cx="60" cy="60" rx="38" ry="56" />
      <line x1="4" y1="60" x2="116" y2="60" />
    </svg>
  );
}
